/**
 * Helper function to get authentication token
 * Tries memory first, then refreshes from cookie if needed
 * This replaces all localStorage.getItem('user').token calls
 */

import { getAccessToken, setAccessToken, isEvictedState } from './tokenManager';
import axios from 'axios';

let refreshPromise = null; // Prevent multiple simultaneous refresh calls
let refreshTokenFailed = false; // Flag to prevent repeated refresh attempts when token is missing

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

/**
 * Shared refresh token function - prevents multiple simultaneous refresh attempts
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export const refreshTokenFromCookie = async () => {
  // Evicted tabs must never silently re-authenticate — this is the one choke
  // point every refresh in the app goes through (proactive refresh, 401 retry,
  // reconnection's token fetch, and any plain REST call via getAuthToken()
  // below), so gating here is what actually makes eviction stick instead of
  // being silently undone by the next ordinary API call. A real page reload
  // resets isEvicted to false (fresh JS module state), so manual re-auth via
  // the still-valid refresh cookie still works as intended.
  if (isEvictedState()) {
    return null;
  }

  // If we've already determined refresh token is missing, don't try again
  if (refreshTokenFailed) {
    return null;
  }

  // If already refreshing, wait for existing promise
  if (refreshPromise) {
    return await refreshPromise;
  }

  // Start new refresh attempt
  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/auth/refresh`,
        {},
        { 
          withCredentials: true,
          timeout: 10000 // Add timeout to prevent hanging requests
        }
      );
      
      const newToken = response.data.accessToken;
      const expiresIn = response.data.expiresIn || 900;
      setAccessToken(newToken, expiresIn);
      
      // Reset failure flag on success
      refreshTokenFailed = false;
      
      return newToken;
    } catch (error) {
      // Only permanently block retries when the server explicitly says the refresh token is gone/expired.
      // Network errors and 5xx responses are transient — let the next attempt through.
      if (error.response?.status === 401 && error.response?.data?.expired) {
        refreshTokenFailed = true;
        localStorage.removeItem('user');
      } else if (!error.response) {
        console.warn('Network error during token refresh, will retry on next attempt');
      }
      // Do not set refreshTokenFailed for 5xx or other transient errors

      if (error.response?.status !== 401 || !error.response?.data?.expired) {
        console.error('Failed to refresh token:', error);
      }

      return null;
    } finally {
      refreshPromise = null; // Reset promise
    }
  })();
  
  return await refreshPromise;
};

/**
 * Get access token from memory or refresh from cookie
 * @returns {Promise<string|null>} Access token or null if unavailable
 */
export const getAuthToken = async () => {
  // First, try to get from memory
  let token = getAccessToken();
  
  if (token) {
    return token;
  }
  
  // If no token in memory, check if we have user data before attempting refresh
  // This prevents unnecessary refresh attempts when there's no session
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    // No user data means no session - don't attempt refresh
    return null;
  }
  
  // If we have user data, try to refresh using cookie
  return await refreshTokenFromCookie();
};

/**
 * Reset the refresh token failure flag (call after successful login)
 */
export const resetRefreshTokenFailure = () => {
  refreshTokenFailed = false;
};

/**
 * Reset the refresh token failure flag on page load
 * This allows refresh attempts after page reloads
 * Call this before validateToken on initial page load
 */
export const resetRefreshTokenFailureOnLoad = () => {
  refreshTokenFailed = false;
};

/**
 * Synchronous version - gets token from memory only (no refresh)
 * Use this when you need immediate token and can handle null
 * @returns {string|null} Access token or null
 */
export const getAuthTokenSync = () => {
  return getAccessToken();
};


